/** Deterministic rail-world surface, measured in metres. The traversable causeway
 * rolls gently along Z; sculpted hills rise only outside the 30m clear corridor.
 * travel is accumulated active rail distance, never presentation/animation time.
 * Freeze that distance for pauses, arrivals, defeats and terminal sequences. */
export const SKY_TERRAIN = Object.freeze({
  roadAmplitude: 0.46,
  roadHalfWidth: 15,
  hillsideBlendWidth: 22,
  groundWidth: 500,
  groundLength: 500,
  groundCenterZ: -70,
  groundColumns: 100,
  groundRows: 100,
  roadColumns: 6,
  roadRows: 140,
});
const finite = (value) => (Number.isFinite(value) ? value : 0);
const smooth = (t) => t * t * t * (t * (t * 6 - 15) + 10);
/** Exact surface height and unit normal, with analytic derivatives. Heights in
 * the flight corridor stay within ±0.46m and its maximum grade is below 2.1 percent.
 * Outside that corridor, broad ridges create real geometry instead of a flat
 * texture. Invalid inputs resolve safely to the origin rather than NaN meshes.
 * Optional output reuses a sample/normal buffer for allocation-free mesh updates. */
export function skyTerrainSample(x, z, travel = 0, output = { normal: [0, 1, 0] }) {
  x = finite(x);
  z = finite(z);
  travel = finite(travel);
  const q = z - 1 - travel;
  const road = 0.34 * Math.sin(q * 0.035) + 0.12 * Math.sin(q * 0.071);
  const roadZ = 0.34 * 0.035 * Math.cos(q * 0.035) + 0.12 * 0.071 * Math.cos(q * 0.071);
  const side = Math.sign(x),
    distance = Math.abs(x);
  const t = Math.max(
    0,
    Math.min(1, (distance - SKY_TERRAIN.roadHalfWidth) / SKY_TERRAIN.hillsideBlendWidth),
  );
  const blend = smooth(t);
  const blendX = t > 0 && t < 1 ? (30 * t * t * (t - 1) ** 2) / SKY_TERRAIN.hillsideBlendWidth : 0;
  const a = q * 0.018 + distance * 0.055,
    b = q * 0.04 - distance * 0.075;
  const hill = 6 + 2.8 * Math.sin(a) + 1.4 * Math.cos(b);
  const dx =
    side * (blendX * hill + blend * (2.8 * 0.055 * Math.cos(a) + 1.4 * 0.075 * Math.sin(b)));
  const dz = roadZ + blend * (2.8 * 0.018 * Math.cos(a) - 1.4 * 0.04 * Math.sin(b));
  const length = Math.hypot(dx, 1, dz);
  output.height = road + blend * hill;
  output.dx = dx;
  output.dz = dz;
  output.normal[0] = -dx / length;
  output.normal[1] = 1 / length;
  output.normal[2] = -dz / length;
  return output;
}
export const skyTerrainHeight = (x, z, travel = 0) => skyTerrainSample(x, z, travel).height;
/** Road-height bound used with the separately measured articulated boss drop.
 * Wide wing tips can extend over the shoulders; actual transformed vertices are
 * also verified against this same terrain sampler throughout their motion. */
export const skyTerrainCombatCeiling = () => SKY_TERRAIN.roadAmplitude;

/** Integrate a bounded exponential ground velocity exactly. The domain owns this
 * state; rendering only reads it, so 30Hz/120Hz and repeated paused draws agree. */
export function skyAdvanceTerrain(travel, speed, target, dt) {
  const velocity = Number.isFinite(speed) ? speed : target;
  if (!Number.isFinite(dt) || dt <= 0) return { travel, speed: velocity };
  const decay = Math.exp(-8 * dt);
  return {
    travel: travel + target * dt + ((velocity - target) * (1 - decay)) / 8,
    speed: target + (velocity - target) * decay,
  };
}

/** Height of the actual roadside mesh triangle, relative to its Y origin. This
 * follows PlaneGeometry's diagonal, not bilinear interpolation. Scenery roots
 * use this surface while corridor physics retain the analytic smooth profile.
 * Out-of-mesh points clamp to the visible edge; an optional sample buffer avoids
 * allocation during instancing. The mesh Y offset remains world-scale-owned. */
export function skyTerrainMeshHeight(x, z, travel = 0, sample = { normal: [0, 1, 0] }) {
  const {
    groundWidth: width,
    groundLength: length,
    groundCenterZ: centre,
    groundColumns: columns,
    groundRows: rows,
  } = SKY_TERRAIN;
  const column = Math.max(0, Math.min(columns, ((finite(x) + width / 2) * columns) / width));
  const row = Math.max(0, Math.min(rows, ((finite(z) - centre + length / 2) * rows) / length));
  const i = Math.min(columns - 1, Math.floor(column)),
    j = Math.min(rows - 1, Math.floor(row));
  const a = column - i,
    b = row - j;
  const x0 = (i * width) / columns - width / 2,
    z0 = centre + (j * length) / rows - length / 2;
  const x1 = x0 + width / columns,
    z1 = z0 + length / rows;
  const h10 = skyTerrainSample(x1, z0, travel, sample).height;
  const h01 = skyTerrainSample(x0, z1, travel, sample).height;
  return a + b <= 1
    ? skyTerrainSample(x0, z0, travel, sample).height * (1 - a - b) + h10 * a + h01 * b
    : skyTerrainSample(x1, z1, travel, sample).height * (a + b - 1) + h10 * (1 - b) + h01 * (1 - a);
}
